import { useEffect, useState } from 'react';
import {
  Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudFog,
  CloudLightning, Snowflake, Droplets, Wind, ArrowUp, ArrowDown, AlertTriangle,
} from 'lucide-react';
import api from '../lib/api';

const ICONS = {
  sun: Sun,
  cloud: Cloud,
  'cloud-sun': CloudSun,
  'cloud-rain': CloudRain,
  'cloud-drizzle': CloudDrizzle,
  'cloud-fog': CloudFog,
  'cloud-lightning': CloudLightning,
  snowflake: Snowflake,
};

function WeatherIcon({ icon, className }) {
  const Cmp = ICONS[icon] || Cloud;
  return <Cmp className={className} />;
}

// Schweregrad → Farben
function severityStyle(sev) {
  const s = (sev || '').toLowerCase();
  if (s === 'extreme' || s === 'severe') return { box: 'bg-[#EF4444]/10 border-[#EF4444]/40', icon: 'text-[#EF4444]' };
  if (s === 'moderate') return { box: 'bg-[#F97316]/10 border-[#F97316]/40', icon: 'text-[#F97316]' };
  return { box: 'bg-[#EAB308]/10 border-[#EAB308]/40', icon: 'text-[#B45309]' };
}

export default function WeatherWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/weather')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    load();
    // Automatische Aktualisierung alle 15 Minuten (hält die „Jetzt"-Stunde aktuell).
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-3 mb-4 animate-pulse h-[72px]" data-testid="weather-widget-loading" />
    );
  }
  if (!data || !data.current) return null;

  const { current, daily = [], hourly = [], alerts = [], location } = data;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-3 mb-4" data-testid="weather-widget">
      {/* Unwetterwarnungen */}
      {alerts.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="weather-alerts">
          {alerts.map((a, i) => {
            const st = severityStyle(a.severity);
            return (
              <div key={i} className={`flex items-start gap-2 rounded-md border p-2 ${st.box}`} data-testid={`weather-alert-${i}`} title={a.description || ''}>
                <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${st.icon}`} />
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-[#1C1C19]">{a.event}</div>
                  {a.headline && <div className="text-[11px] text-[#5C5C59] line-clamp-2">{a.headline}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <WeatherIcon icon={current.icon} className="w-9 h-9 text-[#3B82F6] shrink-0" />
          <div className="min-w-0">
            <div className="flex items-baseline gap-1">
              <span className="font-display text-2xl leading-none text-[#1C1C19]" data-testid="weather-temp">{current.temp}°</span>
              <span className="text-xs text-[#9CA3AF]">gefühlt {current.feels_like}°</span>
            </div>
            <div className="text-xs text-[#5C5C59] truncate">{current.description} · {location}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-xs text-[#5C5C59] shrink-0">
          {daily[0] && (
            <div className="flex items-center gap-1.5">
              <span className="flex items-center"><ArrowUp className="w-3 h-3" />{daily[0].tmax}°</span>
              <span className="flex items-center"><ArrowDown className="w-3 h-3" />{daily[0].tmin}°</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            {daily[0]?.precip_prob != null && <span className="flex items-center gap-0.5"><Droplets className="w-3 h-3" />{daily[0].precip_prob}%</span>}
            {current.wind != null && <span className="flex items-center gap-0.5"><Wind className="w-3 h-3" />{current.wind} km/h</span>}
          </div>
        </div>
      </div>

      {/* Stündliche Vorschau */}
      {hourly.length > 0 && (
        <div className="flex items-stretch gap-0.5 mt-3 pt-3 border-t border-[#F0F0EC] overflow-x-auto -mx-1 px-1" data-testid="weather-hourly">
          {hourly.map((h, i) => (
            <div key={h.time} className="flex flex-col items-center gap-0.5 px-2 shrink-0" data-testid={`weather-hour-${i}`}>
              <span className="text-[10px] text-[#5C5C59] whitespace-nowrap">{i === 0 ? 'Jetzt' : h.hour}</span>
              <WeatherIcon icon={h.icon} className="w-4 h-4 text-[#5C5C59]" />
              <span className="text-xs font-medium text-[#1C1C19] tabular-nums">{h.temp}°</span>
              {h.precip_prob != null && h.precip_prob > 0 ? (
                <span className="text-[9px] text-[#3B82F6] flex items-center gap-0.5 leading-none"><Droplets className="w-2.5 h-2.5" />{h.precip_prob}%</span>
              ) : (
                <span className="text-[9px] leading-none">&nbsp;</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
